import type { Enums } from "@msuite/utils/types";
import { enumeration, json, number, string, table } from "@rocicorp/zero";

/**
 * A wiki video, on the sync path because its row changes without the browser doing anything: Mux
 * tells the server the encode is finished, the server writes the row, and nothing else would tell
 * the page. Before this the block asked every 2.5 seconds for as long as a recording ran.
 *
 * Read-only from here — no mutator writes it. The columns are what the block renders and no more;
 * the signed playback tokens the player presents are minted per request and stored in no column,
 * so they cannot travel this way and are fetched once the row says `ready`.
 */
export const video = table("videos")
	.columns({
		id: string(),
		orgId: string().from("org_id"),
		status: enumeration<Enums<"videoStatus">>(),
		// Null until the encode finishes. The block reads it as "is there something to play".
		muxPlaybackId: string().from("mux_playback_id").optional(),
		// Written when the recording stops and the upload is opened, which is what separates
		// "still recording" from "on its way to Mux" — the row says `uploading` for both.
		muxUploadId: string().from("mux_upload_id").optional(),
		muxAssetId: string().from("mux_asset_id").optional(),
		duration: number().optional(),
		aspectRatio: string().from("aspect_ratio").optional(),
		// The selection a cut left behind. While a render runs it is what is being rendered; once
		// the row is `ready` again and it is still here, the render failed and this is the author's
		// work, waiting to be retried. The block reads it for exactly that: a cut that failed
		// silently reads as a button that does nothing.
		//
		// The one column here that is not on the published video. It is safe to sync where the
		// source identifiers are not: it is a list of numbers the author drew, and it names no
		// asset anybody could play.
		pendingSegments: json().from("pending_segments").optional(),
		createdAt: number().from("created_at"),
	})
	.primaryKey("id");
